'use client';

import { analytics } from '@lib/analytics/page-events';
import { Fire, MagnifyingGlass, TrendUp, X } from 'phosphor-icons';
import {
  FormEvent,
  Fragment,
  KeyboardEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { c } from 'ui-components';
import {
  SUGGESTIONS_SHOWN,
  headerControllers,
} from '../headless/headerSearchEngine';
import { useGhostCompletion } from '../hooks/useGhostCompletion';
import { useSearchHotkey } from '../hooks/useSearchHotkey';
import {
  ConnectedHeaderSearchProps,
  SuggestionItem,
} from './HeaderSearch.types';
import { SearchHotkeyHint } from './SearchHotkeyHint';
import { isImeKeyEvent } from './ghostCompletion';
import { navigateToResults, redirectUrlForQuery } from './searchRedirect';
import { highlightSegments } from './suggestionHighlight';
import { buildTrendingItems, groupSuggestionRuns } from './trendingQueries';

const SUGGESTION_ICON_PROPS = {
  width: 14,
  height: 14,
  weight: 'bold',
  className: 'shrink-0 text-grey-500',
} as const;

const suggestionIcon = (item: SuggestionItem) => {
  if (item.group === 'top') {
    return (
      <Fire
        {...SUGGESTION_ICON_PROPS}
        aria-hidden
        data-test="suggestion-icon-fire"
      />
    );
  }
  if (item.group === 'trending') {
    return (
      <TrendUp
        {...SUGGESTION_ICON_PROPS}
        aria-hidden
        data-test="suggestion-icon-trend-up"
      />
    );
  }
  return <MagnifyingGlass {...SUGGESTION_ICON_PROPS} />;
};

const suggestionSource = (item: SuggestionItem) =>
  item.type === 'trending' ? (item.group ?? 'top') : item.type;

/**
 * The engine-connected header search box: ML query suggestions (and recent
 * queries, when the engine holds any) through the standalone search box
 * controller. On submit or suggestion select it navigates to /search?q=… where
 * the results engine runs the query — it never executes a search itself.
 *
 * Rendered only once the header engine is built (see HeaderSearch.tsx); the
 * plain form covers every moment before that.
 */
export const ConnectedHeaderSearch = ({
  placeholder,
  navigate = navigateToResults,
  initialQuery,
  restoreFocus,
  completionHint,
  completionAcceptedLabel,
  topSearchesLabel,
  trendingSearchesLabel,
  trending,
  onTrendingSlotShown,
  className = '',
}: ConnectedHeaderSearchProps) => {
  const { state, methods } = headerControllers.useStandaloneSearchBox();
  const { state: recentQueriesState } = headerControllers.useRecentQueries();

  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  // The standalone box answers a submit with a redirect URL rather than
  // results; leaving for it is this component's job.
  useEffect(() => {
    if (!state.redirectTo) {
      return;
    }
    const url = redirectUrlForQuery(state.redirectTo, state.value);
    methods?.afterRedirection();
    navigate(url);
  }, [state.redirectTo, state.value, methods, navigate]);

  // This box starts from engine state, which is empty, so the swap out of the
  // plain form would drop what the plain form was showing — the query the
  // results page is rendering, or whatever the shopper had already typed — and
  // their focus with it. Seed both once, and independently: a shopper can be
  // sitting in a still-empty box when the engine lands, and React replacing a
  // focused input drops focus to the document whether or not there was text in
  // it. `updateText` is the only way in (defineStandaloneSearchBox takes no
  // initial state), so it costs one querySuggest call for a seeded query; that
  // dropdown stays closed. Focusing fires handleFocus, so the close comes after
  // it: the shopper is mid-word, not asking for a list over the page behind it,
  // and their next keystroke opens it the usual way. An empty box keeps the
  // open handleFocus gave it: the shopper focused it to see what it lists, and
  // clicking an already focused input would not ask again.
  const seeded = useRef(false);
  useEffect(() => {
    if (seeded.current || !methods || (!initialQuery && !restoreFocus)) {
      return;
    }
    seeded.current = true;
    if (initialQuery) {
      methods.updateText(initialQuery);
    }
    if (restoreFocus) {
      inputRef.current?.focus();
      if (initialQuery) {
        setIsOpen(false);
      }
    }
  }, [initialQuery, restoreFocus, methods]);

  // With a trending arm, an empty box lists trending queries under its recents
  // instead of the controller's raw suggestions; once the shopper types, every
  // arm shows typed suggestions exactly as control does.
  const items = useMemo((): SuggestionItem[] => {
    const list: SuggestionItem[] = [];
    if (state.value.length === 0 && recentQueriesState.queries.length > 0) {
      recentQueriesState.queries.forEach((query, index) => {
        list.push({ type: 'recent', rawValue: query, index });
      });
    }
    if (state.value.length === 0 && trending) {
      list.push(
        ...buildTrendingItems(trending, {
          suggestions: state.suggestions.map(({ rawValue }) => rawValue),
          isLoadingSuggestions: state.isLoadingSuggestions,
          recents: recentQueriesState.queries,
        })
      );
      return list;
    }
    state.suggestions.slice(0, SUGGESTIONS_SHOWN).forEach((suggestion) => {
      list.push({
        type: 'suggestion',
        rawValue: suggestion.rawValue,
        highlightedValue: suggestion.highlightedValue,
      });
    });
    return list;
  }, [
    state.value,
    state.suggestions,
    state.isLoadingSuggestions,
    recentQueriesState.queries,
    trending,
  ]);

  const shouldShowDropdown = isOpen && items.length > 0;

  // The experiment's exposure: every opening of the empty box, in control too,
  // since that is where trending would have appeared. Reported each time rather
  // than once because the receiver may decline (no performance consent yet) and
  // already dedupes per assignment.
  const isEmptyBoxOpen = isOpen && state.value === '';
  useEffect(() => {
    if (isEmptyBoxOpen) {
      onTrendingSlotShown?.();
    }
  }, [isEmptyBoxOpen, onTrendingSlotShown]);

  // Coveo suggestions only: recent queries show for an empty box, where there
  // is nothing to complete. An arrowed-to option hides the ghost so Enter and
  // Escape keep meaning what they did before it existed. After an accept the
  // ghost waits for the next keystroke: otherwise each Tab could take another
  // completion and the shopper would never tab on to the Search button.
  const [completionAccepted, setCompletionAccepted] = useState(false);
  const { completion, inputProps, shouldAccept } = useGhostCompletion({
    inputRef,
    value: state.value,
    suggestion: state.suggestions[0]?.rawValue,
    enabled: shouldShowDropdown && activeIndex === -1 && !completionAccepted,
  });
  const [announcement, setAnnouncement] = useState('');
  const [acceptCount, setAcceptCount] = useState(0);

  // A controlled value change leaves the caret wherever the browser puts it,
  // which is not the end everywhere.
  useLayoutEffect(() => {
    const input = inputRef.current;
    if (acceptCount === 0 || !input) {
      return;
    }
    input.setSelectionRange(input.value.length, input.value.length);
  }, [acceptCount]);

  // The shopper's own casing is kept for what they typed; only the remainder
  // comes from the suggestion, so Tab produces exactly what the box showed.
  const acceptCompletion = () => {
    const next = state.value + completion;
    methods?.updateText(next);
    setActiveIndex(-1);
    setIsOpen(true);
    setAnnouncement(completionAcceptedLabel.replace('{query}', () => next));
    setCompletionAccepted(true);
    setAcceptCount((count) => count + 1);
  };

  // Two ways out of the box, and the dropdown has to follow both. A pointer
  // exit is a mousedown somewhere else on the page; a keyboard exit fires no
  // mouse event at all, so tabbing off the last control would otherwise leave
  // the dropdown floating over whatever the shopper moved on to. focusout
  // bubbles, so the container hears the one that matters: focus leaving an
  // element inside it for anything outside — including a null relatedTarget,
  // which is focus gone to the browser chrome or another window. Selecting a
  // suggestion can't trip it: the options are tabIndex={-1} and their mousedown
  // handler preventDefaults the focus move.
  useEffect(() => {
    const container = containerRef.current;

    const isOutside = (node: EventTarget | null) =>
      !container || !node || !container.contains(node as Node);

    const handleClickOutside = (e: MouseEvent) => {
      if (isOutside(e.target)) {
        setIsOpen(false);
        setActiveIndex(-1);
      }
    };

    const handleFocusOut = (e: FocusEvent) => {
      if (isOutside(e.relatedTarget)) {
        setIsOpen(false);
        setActiveIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    container?.addEventListener('focusout', handleFocusOut);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      container?.removeEventListener('focusout', handleFocusOut);
    };
  }, []);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!state.value.trim()) {
      return;
    }
    methods?.submit();
    setIsOpen(false);
  };

  // A trending arm treats a cleared box like a backspaced one and lists its
  // empty-box content; control closes, as it always has, but still reports the
  // exposure, since trending is what would have opened. clear() drops the
  // typed query's suggestions, so the QS arm asks for the empty query's itself.
  // Focus before settling the open state: focusing the input fires
  // handleFocus, which opens the dropdown. That same synchronous focus reaches
  // handleFocus before React has re-rendered on the cleared engine state, so it
  // still sees the pre-clear text; without this flag it would ask Coveo for
  // suggestions on the now-empty query a second time, or in control at all.
  const clearing = useRef(false);
  const handleClear = () => {
    methods?.clear();
    if (trending?.source === 'qs') {
      methods?.showSuggestions();
    }
    clearing.current = true;
    inputRef.current?.focus();
    clearing.current = false;
    setActiveIndex(-1);
    setIsOpen(Boolean(trending));
    if (!trending) {
      onTrendingSlotShown?.();
    }
  };

  const handleInputChange = (value: string) => {
    methods?.updateText(value);
    setAnnouncement('');
    setCompletionAccepted(false);
    setActiveIndex(-1);
    setIsOpen(true);
  };

  // The QS arm's trending list is Coveo's answer to an empty query, so an
  // empty box asks for it on focus too.
  const handleFocus = () => {
    if (
      !clearing.current &&
      (state.value.length > 0 || trending?.source === 'qs')
    ) {
      methods?.showSuggestions();
    }
    setIsOpen(true);
  };

  const { shortcutKeys, ariaKeyShortcuts } = useSearchHotkey({
    inputRef,
    onActivate: handleFocus,
  });

  // A recent query goes through selectSuggestion like any other item:
  // executeRecentQuery would run a result search on the header engine, whose
  // results nothing renders — and the shopper is about to leave the page.
  const selectItem = useCallback(
    (item: SuggestionItem, index: number) => {
      analytics.pushSearchSuggestionClickEvent({
        suggestion_source: suggestionSource(item),
        suggestion_arm: trending?.source ?? 'none',
        suggestion_position: index + 1,
        suggestion_query: item.rawValue,
      });
      methods?.selectSuggestion(item.rawValue);
      setIsOpen(false);
      setActiveIndex(-1);
    },
    [methods, trending?.source]
  );

  // An IME picks its candidates with the same arrows and Enter the list uses;
  // while one is composing, every key is the IME's.
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (isImeKeyEvent(e.nativeEvent)) {
      return;
    }

    if (shouldAccept(e)) {
      e.preventDefault();
      acceptCompletion();
      return;
    }

    if (!shouldShowDropdown) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        handleFocus();
        // Arrowing a closed list open lands on its first option, so the Enter
        // that follows takes a suggestion instead of submitting the typed text.
        // Only with a list to point at: activeIndex feeds
        // aria-activedescendant, which must not name an option nothing renders.
        if (items.length > 0) {
          setActiveIndex(0);
        }
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex((prev) => (prev < items.length - 1 ? prev + 1 : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex((prev) => (prev > 0 ? prev - 1 : items.length - 1));
        break;
      case 'Enter':
        if (activeIndex >= 0 && activeIndex < items.length) {
          e.preventDefault();
          selectItem(items[activeIndex], activeIndex);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setActiveIndex(-1);
        break;
    }
  };

  const showClearButton = state.value.length > 0;

  const renderSuggestionText = (item: SuggestionItem) => {
    if (item.type === 'suggestion' && item.highlightedValue) {
      return (
        <span>
          {highlightSegments(item.highlightedValue).map((segment, i) =>
            segment.emphasized ? <b key={i}>{segment.text}</b> : segment.text
          )}
        </span>
      );
    }
    return <span>{item.rawValue}</span>;
  };

  const renderOption = ({
    item,
    index,
  }: {
    item: SuggestionItem;
    index: number;
  }) => (
    // No key handler on the option: in the combobox pattern the keys
    // live on the input and reach the list through activeIndex and
    // aria-activedescendant, and the options stay tabIndex={-1} so
    // focus never leaves the box.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events
    <div
      key={`${item.type}-${item.rawValue}`}
      id={`header-search-suggestion-${index}`}
      role="option"
      tabIndex={-1}
      aria-selected={index === activeIndex}
      className={c(
        'flex cursor-pointer items-center gap-3 px-4 py-2.5 text-sm text-grey-300',
        {
          'bg-grey-900': index === activeIndex,
          'hover:bg-grey-900': index !== activeIndex,
        }
      )}
      // mousedown's only job is holding focus in the box: its default
      // would move focus to the option, tripping the container's
      // focusout and closing the list before the click lands.
      // Selection itself hangs off click, so a bare click with no mouse
      // sequence in front of it — assistive tech activating the option,
      // voice control — takes the suggestion too.
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => selectItem(item, index)}
      onMouseEnter={() => setActiveIndex(index)}
    >
      {suggestionIcon(item)}
      {renderSuggestionText(item)}
    </div>
  );

  return (
    <div ref={containerRef} className={c('relative', className)}>
      <form
        onSubmit={handleSubmit}
        role="search"
        data-test="header-search-form"
      >
        <fieldset className="group flex items-stretch overflow-hidden rounded-md border border-grey-600 bg-grey-900 focus-within:border-transparent focus-within:bg-grey-800 focus-within:ring-2 focus-within:ring-blue-hover">
          <legend className="sr-only">{placeholder}</legend>
          <div className="relative flex flex-1 items-center">
            <div className="relative min-w-0 flex-1">
              {completion && (
                <span
                  aria-hidden="true"
                  data-test="header-search-ghost"
                  className="pointer-events-none absolute inset-0 flex items-center overflow-hidden whitespace-pre px-3 py-2 font-medium"
                >
                  <span className="invisible">{state.value}</span>
                  <span className="text-grey-450">{completion}</span>
                </span>
              )}
              <input
                {...inputProps}
                onChange={(e) => handleInputChange(e.target.value)}
                onFocus={handleFocus}
                onKeyDown={handleKeyDown}
                value={state.value}
                type="text"
                placeholder={placeholder}
                aria-label={placeholder}
                aria-keyshortcuts={ariaKeyShortcuts}
                aria-describedby={
                  completion ? 'header-search-completion-hint' : undefined
                }
                className="relative z-10 block w-full bg-transparent px-3 py-2 font-medium text-white placeholder:text-grey-400 focus:outline-none"
                ref={inputRef}
                role="combobox"
                aria-expanded={shouldShowDropdown}
                aria-controls="header-search-suggestions"
                aria-activedescendant={
                  activeIndex >= 0
                    ? `header-search-suggestion-${activeIndex}`
                    : undefined
                }
                autoComplete="off"
              />
            </div>
            <SearchHotkeyHint
              keys={shortcutKeys}
              hidden={state.value.length > 0}
            />
            <button
              type="button"
              className={c('mr-2 shrink-0 p-1 text-grey-400 hover:text-white', {
                hidden: !showClearButton,
              })}
              onClick={handleClear}
              aria-label="Clear search"
            >
              <X height={15} width={15} />
            </button>
          </div>
          <button
            type="submit"
            className="flex w-10 shrink-0 items-center justify-center bg-blue hover:bg-blue-hover"
            aria-label="Search"
          >
            <MagnifyingGlass width={16} height={16} className="text-white" />
          </button>
        </fieldset>
      </form>

      <span id="header-search-completion-hint" className="sr-only">
        {completionHint}
      </span>
      <span role="status" aria-live="polite" className="sr-only">
        {announcement}
      </span>

      {shouldShowDropdown && (
        <div
          id="header-search-suggestions"
          role="listbox"
          className="absolute left-0 top-full z-50 mt-1 w-full overflow-hidden rounded-md border border-grey-700 bg-grey-800"
        >
          {groupSuggestionRuns(items).map(({ group, entries }) =>
            group ? (
              <div
                key={group}
                role="group"
                aria-labelledby={`header-search-group-${group}`}
              >
                <div
                  id={`header-search-group-${group}`}
                  role="presentation"
                  className="px-4 pt-2 text-xs text-grey-400"
                >
                  {group === 'top' ? topSearchesLabel : trendingSearchesLabel}
                </div>
                {entries.map(renderOption)}
              </div>
            ) : (
              <Fragment key={`ungrouped-${entries[0].index}`}>
                {entries.map(renderOption)}
              </Fragment>
            )
          )}
        </div>
      )}
    </div>
  );
};
